import { Outlet } from 'react-router';
import { Button } from '@/components/ui/button';
import { Menu, X, Instagram, Twitter, Linkedin, Home } from 'lucide-react';
import { useState } from 'react';

export function LandingLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isDev = import.meta.env.DEV;
  const protocol = isDev ? 'http' : 'https';
  const port = isDev ? ':3000' : '';
  // In dev: residences.localhost:3000, in prod: residences.unidom.az
  const domain = isDev ? 'residences.localhost' : 'residences.unidom.az';
  const appUrl = `${protocol}://${domain}${port}/`;

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#process' },
    { label: 'FAQ', href: '#faq' },
  ];

  return (
    <div className="min-h-screen flex flex-col font-[Manrope]">
      {/* Fixed Navigation */}
      <header className="fixed top-0 w-full z-50 bg-stone-50/80 backdrop-blur-md border-b border-stone-200">
        <div className="container mx-auto flex h-20 items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-stone-900 flex items-center justify-center">
              <Home className="w-5 h-5 text-stone-50" />
            </div>
            <span className="text-xl font-bold tracking-tight text-stone-900">
              UniDom
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm font-medium text-stone-500 hover:text-stone-900 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Button & Mobile Menu Toggle */}
          <div className="flex items-center gap-4">
            <Button
              asChild
              className="hidden md:inline-flex bg-stone-200 hover:bg-stone-300 text-stone-900 rounded-full px-6 font-medium"
            >
              <a href={appUrl}>Get Started</a>
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-stone-600 hover:text-stone-900 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-stone-50 border-b border-stone-200">
            <div className="container mx-auto px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
              <Button
                asChild
                className="w-full bg-stone-200 hover:bg-stone-300 text-stone-900 rounded-full font-medium mt-4"
              >
                <a href={appUrl}>Get Started</a>
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content - with top padding for fixed nav */}
      <main className="flex-1 pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <a href="/" className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center">
                  <Home className="w-5 h-5 text-stone-400" />
                </div>
                <span className="text-xl font-bold text-stone-100">UniDom</span>
              </a>
              <p className="text-sm text-stone-500 max-w-xs mb-6">
                Modern property management for student residences
              </p>
              {/* Social Icons */}
              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-stone-700 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-stone-700 transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-stone-700 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="text-sm font-semibold text-stone-100 uppercase tracking-wider mb-4">
                Company
              </h4>
              <ul className="space-y-3">
                <li>
                  <a href="#about" className="text-sm hover:text-stone-200 transition-colors">
                    About
                  </a>
                </li>
                <li>
                  <a href="#how-it-works" className="text-sm hover:text-stone-200 transition-colors">
                    How It Works
                  </a>
                </li>
                <li>
                  <a href="#contact" className="text-sm hover:text-stone-200 transition-colors">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Product Column */}
            <div>
              <h4 className="text-sm font-semibold text-stone-100 uppercase tracking-wider mb-4">
                Product
              </h4>
              <ul className="space-y-3">
                <li>
                  <a href="#features" className="text-sm hover:text-stone-200 transition-colors">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="text-sm hover:text-stone-200 transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#api" className="text-sm hover:text-stone-200 transition-colors">
                    API
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div>
              <h4 className="text-sm font-semibold text-stone-100 uppercase tracking-wider mb-4">
                Legal
              </h4>
              <ul className="space-y-3">
                <li>
                  <a href="/privacy" className="text-sm hover:text-stone-200 transition-colors">
                    Privacy
                  </a>
                </li>
                <li>
                  <a href="/terms" className="text-sm hover:text-stone-200 transition-colors">
                    Terms
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="pt-8 border-t border-stone-800">
            <p className="text-sm text-stone-500 text-center">
              © {new Date().getFullYear()} UniDom. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
